import { useUserContext } from 'contexts/user';
import { usePreferencesContext } from 'contexts/preferences';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import { dasherize } from '@zoocasa/node-kit/strings/dasherize';
import { capitalizeWords } from '@zoocasa/node-kit/strings';
import { CountryCode, CountryCodeList } from 'types/countries';

import type { IUserLocation } from 'utils/user-location';
import type SearchPrediction from 'data/search-predictions';
import type { NavigationButton, MenuItem } from '@core/ui/navigation';
import { normalizeDescription } from 'data/search-predictions';

// Location resolution types
export interface ResolvedLocationData {
  cityName: string;
  citySlug: string;
  siteLocation: CountryCode;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  source: 'recentSearch' | 'userLocation' | 'countryDefault';
}

/**
 * TODO: Move this to the core package later.
 * Custom hook for location resolution
 * Resolves location data from various sources with priority order:
 * 1. Recent searches (first priority)
 * 2. User location (second priority) 
 * 3. Country default (fallback)
 */
export function useLocationResolution(): ResolvedLocationData {
  const { recentSearches } = usePreferencesContext();
  const { userLocation, siteLocation } = useUserContext();

  // Location resolution functions
  const resolveLocationFromRecentSearch = (
    recentSearches?: SearchPrediction[]
  ): ResolvedLocationData | null => {
    if (!recentSearches?.length) {
      return null;
    }

    const mostRecent = recentSearches[0];
    if (!mostRecent?.description) {
      return null;
    }

    // Extract clean city name from description
    const parts = mostRecent.description.split(',').map(part => part.trim());
    const cityPart = parts[0];
    
    if (!cityPart) {
      return null;
    }

    const cityName = capitalizeWords(cityPart);
       
       
    // Use urlPath as primary source, fall back to description
    let citySlug: string;
    if (mostRecent.urlPath) {
      // Extract slug from urlPath (e.g., "/waterloo-on-real-estate" -> "waterloo-on")
      citySlug = mostRecent.urlPath.replace(/^\//, '').replace('-real-estate', '');
    } else {
      // Fall back to description processing
      const normalizedDescription = normalizeDescription(mostRecent.description);
      citySlug = dasherize(normalizedDescription.replaceAll(',', '').toLowerCase());
    }


    return {
      cityName,
      citySlug,
      siteLocation,
      coordinates: {
        latitude: mostRecent.data?.position?.latitude || mostRecent.data?.position?.coordinates?.[1] || 0,
        longitude: mostRecent.data?.position?.longitude || mostRecent.data?.position?.coordinates?.[0] || 0,
      },
      source: 'recentSearch',
    };
  };

  const resolveLocationFromUserLocation = (
    userLocation?: IUserLocation
  ): ResolvedLocationData | null => {    
    if (!userLocation?.name) {
      return null;
    }

    // Extract clean city name, removing any province/state info
    const parts = userLocation.name.split(',').map(part => part.trim());
    const cityPart = parts[0];
    
    if (!cityPart) {
      return null;
    }

    const cityName = capitalizeWords(cityPart);

    return {
      cityName,
      citySlug: userLocation.slug || dasherize(cityName.toLowerCase()),
      siteLocation,
      coordinates: {
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      },
      source: 'userLocation',
    };
  };

  const resolveLocationFromCountryDefault = (): ResolvedLocationData => {
    const defaultLocation = siteLocation === CountryCodeList.UNITED_STATES ? 
      defaultUSCityPayload : 
      defaultCACityPayload;
    
    return {
      cityName: defaultLocation.name,
      citySlug: defaultLocation.slug,
      siteLocation,
      coordinates: {
        latitude: defaultLocation.latitude,
        longitude: defaultLocation.longitude,
      },
      source: 'countryDefault',
    };
  };

  // Main location resolution with priority order
  // Try recent search first
  const fromRecentSearch = resolveLocationFromRecentSearch(recentSearches);
  if (fromRecentSearch) {
    return fromRecentSearch;
  }

  // Try user location second
  const fromUserLocation = resolveLocationFromUserLocation(userLocation);
  if (fromUserLocation) {
    return fromUserLocation;
  }

  // Fallback to country default
  return resolveLocationFromCountryDefault();
}

/**
 * Process navigation items by replacing templates with actual location data
 * This utility processes navigation items before passing them to navigation components
 */
export function processNavigationItemsWithLocation<T extends NavigationButton[] | MenuItem[]>(
  items: T, 
  locationData?: ResolvedLocationData
): T {
  if (!locationData) {
    return items;
  }
  
  const rawCityName = locationData.cityName || 'City';
  const citySlug = locationData.citySlug || 'search';
  
  // Use clean city name for display (strip province/state)
  const displayCityName = rawCityName.split(',')[0].trim();

  return items.map(item => {
    if ('buttonVariant' in item && item.buttonVariant === 'divider') {
      return item;
    }
    if ('type' in item && item.type === 'divider') {
      return item;
    }
    
    // Use clean city name for labels, keep full slug for URLs
    const label = item.template?.replace('{city}', displayCityName) || item.label || '';
    const href = item.href?.replace('{city-slug}', citySlug).replace('{city}', displayCityName).replace('{site-location}', locationData.siteLocation.toLowerCase()) || item.href;
    
    return {
      ...item,
      label,
      href,
      items: item.items ? processNavigationItemsWithLocation(item.items, locationData) : undefined,
    };
  }) as T;
} 